import { HttpClient } from '@angular/common/http';
import { Component, Input, ViewEncapsulation } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material';
import { ActivatedRoute } from '@angular/router';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { VideoInfoModalService } from '@core/components/video-info-modal/video-info-modal.service';
import { CustomMessagesService } from '@core/errors/custom-messages.service';
import { CognitoResponse, LoginCustomization } from '@core/interfaces';
import { NavigationService } from '@core/services/navigation/navigation.service';
import { PushNotificationsService } from '@core/services/push-notifications/push-notifications.service';
import { SocketService } from '@core/services/socket.service';
import { delay } from '@core/utils/delay';
import { getErrorCode } from '@core/utils/get-error-code';
import { FuseProgressBarService } from '@fuse/components/progress-bar/progress-bar.service';
import { PasswordValidation } from '@validators/password-validation';
import { ReadRecoveryCodesComponent } from 'app/main/settings/profile/dialogs/read-recovery-codes-dialog/read-recovery-codes.component';
import { MfaService } from 'app/main/settings/profile/services/mfa.service';
import { sentry } from 'sentry';
import { environment } from '../../../../../environments/environment';
import { slideInRight } from '../../animations/slide-in-right.animation';
import { UserLookupResult, UserLookupService } from '../../services/api/user-lookup.api.service';
import { LoginResolver } from '../../services/login.resolver';

type LoginStep = 'email'
  | 'password'
  | 'softwareToken'
  | 'recoveryCode'
  | 'resetPassword'
  | 'expiredPassword'
  | 'expiredSoonPassword'
  | 'successOnResetPassword';

@Component({
  selector: 'login-auth',
  templateUrl: './auth.component.html',
  styleUrls: ['./auth.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [slideInRight]
})
export class LoginAuthComponent {
  public isLoading: boolean;
  public loginStep: LoginStep;
  public loginForm: FormGroup;
  public lookupResult: UserLookupResult;
  public extraAuthInfo: CognitoResponse;

  public resetPasswordForm: FormGroup;
  public recoveryCodeInput: FormControl;
  public verificationCodeForm: FormArray;

  @Input()
  public loginCustomization: LoginCustomization;

  constructor(
    private readonly matDialog: MatDialog,
    private readonly mfaService: MfaService,
    private readonly formBuilder: FormBuilder,
    private readonly socketService: SocketService,
    private readonly loginResolver: LoginResolver,
    private readonly activatedRoute: ActivatedRoute,
    private readonly userLookupService: UserLookupService,
    private readonly navigationService: NavigationService,
    private readonly fuseProgressBar: FuseProgressBarService,
    private readonly customMessageService: CustomMessagesService,
    private readonly authenticationService: AuthenticationService,
    private readonly pushNotificationsService: PushNotificationsService,
    private readonly videoInfoModalService: VideoInfoModalService,
    private http: HttpClient
  ) {
    this.loginStep = 'email';

    this.isLoading = false;

    this.lookupResult = null;
    this.extraAuthInfo = null;
    this.recoveryCodeInput = null;
    this.verificationCodeForm = null;

    this.loginCustomization = this.loginResolver.loginCustomization;
    const email = this.activatedRoute.snapshot.queryParams.email || null;
    this.loginForm = this.formBuilder.group({
      email: [email, [Validators.required, Validators.email, Validators.pattern(/@.+\./)]],
      password: [null, [Validators.required]]
    });
  }

  public async authWithAd(): Promise<void> {
    const { email } = this.loginForm.getRawValue();

    const openIdConfiguration = await this.http.get<any>(environment.MORANNON_URL + '/open-id-configuration', {
      params: {
        slug: this.lookupResult.slug
      }
    }).toPromise();

    const url = new URL(openIdConfiguration.authorization_endpoint);
    url.searchParams.append('client_id', this.lookupResult.clientId);
    url.searchParams.append('response_type', 'id_token');
    url.searchParams.append('redirect_uri', `${environment.MOUNTDOOM_URL}/auth-redirect/index.html`);
    url.searchParams.append('scope', 'openid profile email');
    url.searchParams.append('nonce', Math.random().toString(36).substring(7));
    url.searchParams.append('login_hint', email);
    url.searchParams.append('state', encodeURIComponent(JSON.stringify({
      provider: 'active-directory',
      callbackUrl: `${environment.MOUNTDOOM_URL}/#/login/redirecionando`
    })));
    location.href = url.toString();
  }

  public async authWithGoogle(): Promise<void> {
    const { email } = this.loginForm.getRawValue();

    const url = new URL('https://accounts.google.com/o/oauth2/v2/auth');
    url.searchParams.append('client_id', '508717818542-s5m9fdbsi71kicmssor6fd2ojeijki8p.apps.googleusercontent.com');
    url.searchParams.append('redirect_uri', `https://fornecedores.fieldcontrol.com.br/auth-redirect/index.html`);
    url.searchParams.append('response_type', 'id_token');
    url.searchParams.append('scope', 'openid profile email');
    url.searchParams.append('access_type', 'offline');
    url.searchParams.append('nonce', Math.random().toString(36).substring(7));
    url.searchParams.append('login_hint', email);
    url.searchParams.append('prompt', 'select_account');
    url.searchParams.append('state', encodeURIComponent(JSON.stringify({
      provider: 'google',
      callbackUrl: `${environment.MOUNTDOOM_URL}/#/login/redirecionando`
    })));
    location.href = url.toString();
  }

  public async auth(): Promise<void> {
    try {
      this.isLoading = true;
      this.fuseProgressBar.show();

      const { email, password } = this.loginForm.getRawValue();
      this.loginForm.get('password').markAsTouched();

      await this.authenticationService.login({ provider: 'email-password', email, password });
      await this.refreshItemsAndConnectSocket();
    } catch (err) {
      const code = getErrorCode(err);

      if (code === 'NewPasswordRequired') {
        this.toNewPasswordStep();
        return;
      }

      if (code === 'SoftwareTokenMfaRequired') {
        this.toSoftwareTokenStep();
        return;
      }

      if (code === 'ExpiredPassword') {
        this.toNewPasswordStep(true);
        return;
      }

      if (code === 'ExpiredPasswordSoon') {
        this.extraAuthInfo = err.error.response;
        this.loginStep = 'expiredSoonPassword';
        return;
      }

      if (code === 'NotAuthorizedException') {
        this.setLoginError({ message: err.error.message, code: code });
        return;
      }

      this.setLoginError({ message: err.error.message, code: code });

      if (this.loginForm.get('password').valid) {
        this.loginForm.get('password').setErrors({ unknown: true });
      }
      console.log('[DEBUG ERROR]: ', err);

      sentry.captureException(err, {
        user: {
          email: this.loginForm.get('email').value
        },
        extra: {
          message: 'Error on authentication',
          err: JSON.stringify(err)
        }
      });
    } finally {
      this.isLoading = false;
      this.fuseProgressBar.hide();
    }
  }

  public async authWithSoftwareToken(): Promise<void> {
    try {
      this.isLoading = true;
      this.fuseProgressBar.show();

      await this.authenticationService.loginWithSoftwareToken({
        email: this.loginForm.get('email').value,
        code: this.verificationCodeForm.value.join('')
      });

      await this.refreshItemsAndConnectSocket();
    } catch (err) {
      const code = getErrorCode(err);
      if (code === 'CodeMismatchException') {
        this.verificationCodeForm.setErrors({ incorrect: true });
        return;
      }

      if (code === 'NotAuthorizedException') {
        this.loginForm.reset();
        this.loginStep = 'email';
        this.customMessageService.error('LOGIN.AUTH.MFA.EXPIRED_CODE');
        this.verificationCodeForm.reset();
        return;
      }

      if (code === 'UnauthorizedUser') {
        this.loginForm.reset();
        this.loginStep = 'email';
        this.customMessageService.error('LOGIN.AUTH.ERRORS.UNAUTHORIZED');
        this.verificationCodeForm.reset();
        return;
      }

      this.customMessageService.error('LOGIN.AUTH.ERRORS.UNKNOWN');
      console.error(err);

      sentry.captureException(err, {
        user: {
          email: this.loginForm.get('email').value
        },
        extra: {
          message: 'Error on authentication with verification code',
          err: JSON.stringify(err)
        }
      });
    } finally {
      this.isLoading = false;
      this.fuseProgressBar.hide();
    }
  }

  public async authWithRecoveryCode(): Promise<void> {
    try {
      this.isLoading = true;
      this.fuseProgressBar.show();
      this.recoveryCodeInput.markAsTouched();

      await this.authenticationService.loginWithRecoveryCode({
        email: this.loginForm.get('email').value,
        password: this.loginForm.get('password').value,
        recoveryCode: this.recoveryCodeInput.value
      });

      const recoveryCodes = await this.mfaService.getRecoveryCodes({
        accessToken: this.authenticationService.accessToken
      });

      await this.refreshItemsAndConnectSocket();

      const allCodesIsUsed = recoveryCodes.every(({ status }) => status === 'CURRENT_USED');
      if (allCodesIsUsed) {
        this.matDialog
          .open(ReadRecoveryCodesComponent, {
            panelClass: 'read-recovery-codes',
            disableClose: true,
            autoFocus: false,
            data: {
              recoveryCodes,
              me: this.authenticationService.user,
              type: 'all-used',
              step: 2,
            }
          });
      }
    } catch (err) {
      const code = getErrorCode(err);
      if (['InvalidRecoveryCode', 'RecoveryCodeMismatch'].includes(code)) {
        this.recoveryCodeInput.setErrors({ incorrect: true });
        return;
      }

      if (code === 'RecoveryCodeRevoked') {
        this.recoveryCodeInput.setErrors({ revoked: true });
        return;
      }

      if (code === 'RecoveryCodeAlreadyUsed') {
        this.recoveryCodeInput.setErrors({ used: true });
        return;
      }

      sentry.captureException(err, {
        user: {
          email: this.loginForm.get('email').value
        },
        extra: {
          message: 'Error on authentication with recovery code',
          err: JSON.stringify(err)
        }
      });
    } finally {
      this.isLoading = false;
      this.fuseProgressBar.hide();
    }
  }

  public async authWithExpiringPassword(): Promise<void> {
    if (this.extraAuthInfo.challengeName === 'SOFTWARE_TOKEN_MFA') {
      this.toSoftwareTokenStep();
      return;
    }

    try {
      this.isLoading = true;
      this.fuseProgressBar.show();

      await this.authenticationService.updateStateAndSetRefreshToken(this.extraAuthInfo);
      await this.refreshItemsAndConnectSocket();
    } catch (err) {
      this.loginStep = 'email';
      sentry.captureException(err);
    } finally {
      this.isLoading = false;
      this.fuseProgressBar.hide();
    }
  }

  public async createNewPassword(): Promise<void> {
    try {
      const input = {
        email: this.loginForm.get('email').value,
        newPassword: this.resetPasswordForm.get('password').value
      };

      this.isLoading = true;
      this.fuseProgressBar.show();

      this.loginForm.reset();
      this.resetPasswordForm.reset();
      await this.authenticationService.createNewPassword(input);
      this.loginStep = 'successOnResetPassword';
    } catch (err) {
      const code = getErrorCode(err);
      if (['InvalidParameterException', 'InvalidPasswordException'].includes(code)) {
        this.resetPasswordForm.get('password').setErrors({ pattern: true });
        return;
      }

      if (code === 'SoftwareTokenMfaRequired') {
        this.toSoftwareTokenStep();
        return;
      }

      if (code === 'LimitExceededException') {
        this.resetPasswordForm.setErrors({ limitExceeded: true });
        return;
      }

      this.resetPasswordForm.setErrors({ unknown: true });
      sentry.captureException(err, {
        user: {
          email: this.loginForm.get('email').value
        },
        extra: {
          message: 'Error on createNewPasswordAfterTemporary',
          err: JSON.stringify(err)
        },
      });
    } finally {
      this.isLoading = false;
      this.fuseProgressBar.hide();
    }
  }

  public async createNewPasswordAfterExpiredAndLogin(): Promise<void> {
    try {
      this.isLoading = true;
      this.fuseProgressBar.show();

      const newPassword = this.resetPasswordForm.get('password').value;
      this.extraAuthInfo = await this.authenticationService.createNewPasswordAfterExpiredAndLogin(newPassword);
      await this.authenticationService.updateStateAndSetRefreshToken(this.extraAuthInfo);
      await this.refreshItemsAndConnectSocket();
    } catch (err) {
      const code = getErrorCode(err);

      if (code === 'NewUserPasswordHasBeenUsedBefore') {
        this.resetPasswordForm.get('password').setErrors({ usedBefore: true });
        return;
      }

      if (code === 'SignInWithNewPasswordInvalidSession') {
        this.loginStep = 'email';
        return;
      }

      if (['InvalidParameterException', 'InvalidPasswordException'].includes(code)) {
        this.resetPasswordForm.get('password').setErrors({ pattern: true });
        return;
      }

      if (code === 'SoftwareTokenMfaRequired') {
        this.toSoftwareTokenStep();
        return;
      }

      if (code === 'LimitExceededException') {
        this.resetPasswordForm.setErrors({ limitExceeded: true });
        return;
      }

      this.resetPasswordForm.setErrors({ unknown: true });
      sentry.captureException(err, {
        user: {
          email: this.loginForm.get('email').value
        },
        extra: {
          message: 'Error on createNewPasswordAfterExpiredAndLogin',
          err: JSON.stringify(err)
        },
      });
    } finally {
      this.isLoading = false;
      this.fuseProgressBar.hide();
    }
  }

  public onClickBackToLogin(): void {
    this.loginStep = 'email';
  }

  private setLoginError({ message, code }): void {
    const mappedErrors = {
      'User is disabled.': 'disabled',
      'Password attempts exceeded': 'passwordAttemptsExceeded',
      'UserNotFoundException': 'failed',
      'Incorrect username or password.': 'failed',
      'UnauthorizedUser': 'unauthorized',
      'Password reset required for the user': 'passwordResetRequired',
      'Temporary password has expired and must be reset by an administrator.': 'temporaryPasswordExpired',
    };

    const error = mappedErrors[message] || mappedErrors[code] || 'unknown';
    this.loginForm.get('password').setErrors({
      [error]: true
    });

  }

  public async toPasswordStep(): Promise<void> {
    try {
      this.isLoading = true;
      this.fuseProgressBar.show();
      this.loginForm.get('email').markAsTouched();

      const email = this.loginForm.value.email.trim();
      this.lookupResult = await this.userLookupService.lookupUserByEmail(email);
      if (this.lookupResult.code === 'not_found') {
        this.lookupResult.name = email.split(/@|\./g)[0];
      }

      if (this.lookupResult.loginIntegrationEnabled) {
        if (this.lookupResult.loginProvider === 'active-directory') {
          this.authWithAd();
        }

        if (this.lookupResult.loginProvider === 'google') {
          this.authWithGoogle();
        }

      } else {
        this.loginStep = 'password';
        await delay(300);
        const passwordInput = document.querySelector('input[type=password]') as HTMLInputElement;
        if (passwordInput) {
          passwordInput.focus();
        }

        this.isLoading = false;
        this.fuseProgressBar.hide();

      }
    } catch (err) {
      sentry.captureException(err, {
        user: {
          email: this.loginForm.get('email').value
        },
        extra: {
          message: 'Error on login',
          err: JSON.stringify(err)
        }
      });

      this.isLoading = false;
      this.fuseProgressBar.hide();
    }
  }

  public toRecoveryCodeStep(): void {
    this.recoveryCodeInput = new FormControl(null, [Validators.required]);
    this.loginStep = 'recoveryCode';
  }

  public toSoftwareTokenStep(): void {
    this.verificationCodeForm = this.formBuilder.array([
      new FormControl(null, [Validators.required]),
      new FormControl(null, [Validators.required]),
      new FormControl(null, [Validators.required]),
      new FormControl(null, [Validators.required]),
      new FormControl(null, [Validators.required]),
      new FormControl(null, [Validators.required])
    ]);

    this.loginStep = 'softwareToken';
  }

  public toNewPasswordStep(isExpired?: boolean): void {
    this.resetPasswordForm = this.formBuilder.group({
      password: [null, [
        Validators.required,
        Validators.minLength(8),
        Validators.pattern(new RegExp('[0-9]{1,}')),
        Validators.pattern(new RegExp('[a-z]{1,}')),
        Validators.pattern(new RegExp('[A-Z]{1,}')),
        Validators.pattern(/\^|\$|\*|\.|\[|\]|\{|\}|\(|\)|\?|\-|\"|\!|\@|\#|\%|\&|\/|\\|\,|\>|\<|\'|\:|\;|\||\_|\~|\`|\+|\='/)
      ]],
      confirmPassword: [null, Validators.required]
    }, {
      validator: PasswordValidation.validate.bind(this)
    });

    this.loginStep = isExpired ? 'expiredPassword' : 'resetPassword';
  }

  private async refreshItemsAndConnectSocket(): Promise<void> {
    const firstVisibleItem = this.navigationService.getFirstVisibleItem(true);
    const redirectToDestinationUrl = localStorage.getItem('REDIRECT_TO_URL_AFTER_LOGIN');
    location.href = redirectToDestinationUrl || firstVisibleItem.url;
    if (redirectToDestinationUrl) {
      localStorage.removeItem('REDIRECT_TO_URL_AFTER_LOGIN');
    }

    this.navigationService.showSupplierIndicationPopUpForCurrentMonth();
    await this.pushNotificationsService.refreshNotificationSubscription();
    await this.socketService.reconnect();
    window.dispatchEvent(new Event('refresh-notification-count'));
  }
}
